import React, { useState } from 'react';
import { Tenant } from '../../services/tenantService';
import { useModal } from '../../contexts/ModalContext';
import Pagination from '../Common/Pagination';
import { usePagination } from '../../hooks/usePagination';
import TenantForm from './TenantForm';
import './TenantsTable.css';

interface TenantsTableProps {
  tenants: Tenant[];
  onUpdateTenant: (id: number, data: any) => Promise<void>;
  onDeleteTenant: (id: number) => Promise<void>;
  onToggleStatus: (id: number) => Promise<void>;
  onRegenerateApiKey: (id: number) => Promise<void>;
  loading?: boolean;
}

const TenantsTable: React.FC<TenantsTableProps> = ({
  tenants,
  onUpdateTenant,
  onDeleteTenant,
  onToggleStatus,
  onRegenerateApiKey,
  loading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all');
  const [visibleTokens, setVisibleTokens] = useState<Set<number>>(new Set());
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const { showModal } = useModal();

  // Filtrer les tenants
  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = tenant.nomTenant.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && !tenant.disabled) ||
                         (statusFilter === 'disabled' && tenant.disabled);
    
    return matchesSearch && matchesStatus;
  });

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    totalItems,
    setCurrentPage,
    setItemsPerPage
  } = usePagination({
    data: filteredTenants,
    itemsPerPage: 10,
    initialPage: 1
  });

  const maskToken = (token: string | undefined) => {
    if (!token || token.length <= 8) return '••••••••';
    return token.substring(0, 4) + '••••••••' + token.substring(token.length - 4);
  };

  const toggleTokenVisibility = (tenantId: number) => {
    const newVisibleTokens = new Set(visibleTokens);
    if (newVisibleTokens.has(tenantId)) {
      newVisibleTokens.delete(tenantId);
    } else {
      newVisibleTokens.add(tenantId);
    }
    setVisibleTokens(newVisibleTokens);
  };

  const handleToggleStatus = async (id: number) => {
    const tenant = tenants.find(t => t.id === id);
    const action = tenant?.disabled ? 'activer' : 'désactiver';
    
    showModal({
      title: `Confirmer ${action}`,
      message: `Êtes-vous sûr de vouloir ${action} le tenant "${tenant?.nomTenant}" ?`,
      type: 'confirm',
      confirmText: action === 'activer' ? 'Activer' : 'Désactiver',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await onToggleStatus(id);
        } catch (err: any) {
          showModal({
            title: 'Erreur de changement de statut',
            message: err.response?.data?.message || 'Erreur lors du changement de statut',
            type: 'error',
            confirmText: 'OK'
          });
        }
      }
    });
  };

  const handleRegenerateApiKey = async (id: number) => {
    const tenant = tenants.find(t => t.id === id);
    
    showModal({
      title: 'Confirmer la régénération',
      message: `Êtes-vous sûr de vouloir régénérer la clé API du tenant "${tenant?.nomTenant}" ? L'ancienne clé ne fonctionnera plus.`,
      type: 'confirm',
      confirmText: 'Régénérer',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await onRegenerateApiKey(id);
        } catch (err: any) {
          showModal({
            title: 'Erreur de régénération',
            message: err.response?.data?.message || 'Erreur lors de la régénération de la clé API',
            type: 'error',
            confirmText: 'OK'
          });
        }
      }
    });
  };

  const handleDelete = async (id: number) => {
    const tenant = tenants.find(t => t.id === id);
    showModal({
      title: 'Confirmer la suppression',
      message: `Êtes-vous sûr de vouloir supprimer le tenant "${tenant?.nomTenant}" ? Cette action est irréversible.`,
      type: 'confirm',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await onDeleteTenant(id);
        } catch (err: any) {
          showModal({
            title: 'Erreur de suppression',
            message: err.response?.data?.message || 'Erreur lors de la suppression du tenant',
            type: 'error',
            confirmText: 'OK'
          });
        }
      }
    });
  };

  const handleEdit = (tenant: Tenant) => {
    setEditingTenant(tenant);
  };

  const handleUpdate = async (id: number, data: { nomTenant: string; chaineConnexion: string }) => {
    try {
      await onUpdateTenant(id, data);
      setEditingTenant(null);
      showModal({
        title: 'Tenant mis à jour',
        message: `Le tenant "${data.nomTenant}" a été mis à jour avec succès.`,
        type: 'success',
        confirmText: 'OK'
      });
    } catch (err: any) {
      showModal({
        title: 'Erreur de mise à jour',
        message: err.response?.data?.message || 'Erreur lors de la mise à jour du tenant',
        type: 'error',
        confirmText: 'OK'
      });
    }
  };

  if (loading) {
    return <div className="loading">Chargement des tenants...</div>;
  }

  return (
    <div className="tenants-table-container">
      <div className="table-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Rechercher par nom de tenant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-container">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'disabled')}
            className="filter-select"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Activés uniquement</option>
            <option value="disabled">Désactivés uniquement</option>
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="tenants-table">
          <thead>
            <tr>
              <th>Nom du tenant</th>
              <th>Chaîne de connexion</th>
              <th>Clé API</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((tenant) => (
              <tr key={tenant.id}>
                <td>
                  <strong>{tenant.nomTenant}</strong>
                </td>
                <td>
                  <span className="connection-string" title={tenant.chaineConnexion}>
                    {tenant.chaineConnexion ? 
                      (tenant.chaineConnexion.length > 50 ? 
                        tenant.chaineConnexion.substring(0, 50) + '...' : 
                        tenant.chaineConnexion
                      ) : 
                      'Non définie'
                    }
                  </span>
                </td>
                <td>
                  <code className="api-key">
                    {visibleTokens.has(tenant.id) ? tenant.apiKey : maskToken(tenant.apiKey)}
                  </code>
                </td>
                <td>
                  <span className={`status ${tenant.disabled ? 'disabled' : 'active'}`}>
                    {tenant.disabled ? 'Désactivé' : 'Activé'}
                  </span>
                </td>
                <td>
                  <div className="table-actions">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(tenant)}
                      title="Modifier le tenant"
                    >
                      ✏️
                    </button>
                    <button
                      className="action-btn view"
                      onClick={() => toggleTokenVisibility(tenant.id)}
                      title={visibleTokens.has(tenant.id) ? "Masquer la clé" : "Afficher la clé"}
                    >
                      {visibleTokens.has(tenant.id) ? '👁️' : '👁️‍🗨️'}
                    </button>
                    <button
                      className="action-btn regenerate"
                      onClick={() => handleRegenerateApiKey(tenant.id)}
                      title="Régénérer la clé API"
                    >
                      🔄
                    </button>
                    <button
                      className="action-btn toggle"
                      onClick={() => handleToggleStatus(tenant.id)}
                      title={tenant.disabled ? "Activer" : "Désactiver"}
                    >
                      {tenant.disabled ? "🔓" : "🔒"}
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(tenant.id)}
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginatedData.length === 0 && (
        <div className="empty-state">
          {searchTerm || statusFilter !== 'all' 
            ? 'Aucun tenant ne correspond aux critères de recherche.'
            : 'Aucun tenant trouvé.'
          }
        </div>
      )}

      {editingTenant && (
        <TenantForm
          tenant={editingTenant}
          onSubmit={(data) => handleUpdate(editingTenant.id, data)}
          onCancel={() => setEditingTenant(null)}
        />
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onItemsPerPageChange={setItemsPerPage}
      />
    </div>
  );
};

export default TenantsTable;
